import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, Gift, Search, UserRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import useAdminAutoRefresh from "../../lib/useAdminAutoRefresh";
import VerifiedName from "../../components/VerifiedName";

const money = (value) => `$${((Number(value) || 0) / 1000).toFixed(2)}`;

function grantAmount(row) {
  const units = Number(row?.amount || 0);
  if (!units) return "$0.00";
  const sign = String(row?.type || "").includes("debit") ? "-" : "";
  return `${sign}${money(units)}`;
}

function grantReason(row) {
  const type = String(row?.type || row?.transfer_kind || "");
  if (type.includes("phone_verification")) return "Phone verification";
  if (type.includes("referral")) return "Referral";
  if (type.includes("debit")) return "Manual debit";
  return "Manual grant";
}

function grantFee(row) {
  const value = row?.redeem_fee_percent;
  if (value === undefined || value === null || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return `${value}%`;
  const label = Number.isInteger(number) ? String(number) : number.toFixed(2).replace(/\.?0+$/, "");
  return `${label}%`;
}

function grantGame(row) {
  return row?.game_name || row?.game_id || "Player choice";
}

export default function AdminFreeCredits() {
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [userId, setUserId] = useState("");
  const [amount, setAmount] = useState("5");
  const [redeemFee, setRedeemFee] = useState("20");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadUsers = useCallback(async () => {
    const { data } = await api.get("/admin/users");
    setUsers(data.filter((u) => u.role === "user"));
  }, []);

  const loadGrants = useCallback(async () => {
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    const { data } = await api.get(`/admin/free-play/grants?${params.toString()}`);
    setRows(data);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadUsers(), loadGrants()])
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [loadUsers, loadGrants]);

  useAdminAutoRefresh(() => Promise.all([loadUsers(), loadGrants()]));

  const filteredUsers = useMemo(() => {
    const term = memberSearch.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) =>
      `${u.username || ""} ${u.email || ""}`.toLowerCase().includes(term),
    );
  }, [memberSearch, users]);

  const grant = async (event) => {
    event.preventDefault();
    const dollarAmount = Number(amount);
    const feePercent = Number(redeemFee);
    if (!userId) {
      toast.error("Pick a member first");
      return;
    }
    if (!Number.isFinite(dollarAmount) || dollarAmount <= 0) {
      toast.error("Enter a valid free-credit amount");
      return;
    }
    if (!Number.isFinite(feePercent) || feePercent < 0 || feePercent > 100) {
      toast.error("Enter a valid redeem fee from 0 to 100");
      return;
    }

    setSaving(true);
    try {
      const { data } = await api.post("/admin/free-play/grant", {
        user_id: userId,
        amount: dollarAmount,
        redeem_fee_percent: feePercent,
        note: note.trim(),
      });
      setRows((current) => [data.transaction, ...current]);
      setUsers((current) => current.map((u) => (u.id === userId ? data.user : u)));
      setNote("");
      toast.success(`Free credits granted to ${data.user.username}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3 text-slate-200 sm:p-6 md:p-8">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-[#F1C40F]" />
            <h1 className="text-2xl font-bold tracking-tight text-white">Free Credits</h1>
          </div>
          <p className="mt-1 text-sm text-slate-400">Manual grants and free-credit claim history.</p>
        </div>

        <div className="relative w-full lg:w-[400px]">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search grants"
            className="h-11 w-full rounded-2xl border border-white/10 bg-[#0B0B0D] pl-11 pr-4 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-400 focus:border-[#D4AF37]/70"
          />
        </div>
      </div>

      <form onSubmit={grant} className="mb-5 rounded-2xl border border-[#3A3216]/80 bg-[#101010] p-4 shadow-[0_0_0_1px_rgba(212,175,55,0.03)] sm:p-5">
        <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Grant free credits</h2>
            <p className="mt-1 text-sm text-slate-400">Pick a member, amount, and redeem fee. The player chooses the game when claiming.</p>
          </div>
          <button
            disabled={saving}
            className="inline-flex h-11 items-center justify-center rounded-2xl bg-[#F5A400] px-6 text-sm font-semibold text-black transition-colors hover:bg-[#FFC247] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving ? "Granting..." : "Grant free credits"}
          </button>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-white">Search member</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder="Username or email"
                className="h-11 w-full rounded-2xl border border-white/10 bg-[#090909] pl-11 pr-12 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-400 focus:border-[#D4AF37]/70"
              />
              <UserRound className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
            </div>
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-white">Member</span>
            <div className="relative">
              <select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="h-11 w-full appearance-none rounded-2xl border border-white/10 bg-[#090909] px-4 pr-11 text-sm text-slate-100 outline-none transition-colors focus:border-[#D4AF37]/70"
              >
                <option value="">Pick member</option>
                {filteredUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username} ({u.email}) {u.phone_verified || u.email_verified ? "" : "- not verified"}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            </div>
          </label>
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-white">Amount</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="h-11 w-full rounded-2xl border border-white/10 bg-[#090909] px-4 text-sm text-slate-100 outline-none transition-colors focus:border-[#D4AF37]/70"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-white">Redeem fee %</span>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={redeemFee}
              onChange={(e) => setRedeemFee(e.target.value)}
              className="h-11 w-full rounded-2xl border border-white/10 bg-[#090909] px-4 text-sm text-slate-100 outline-none transition-colors focus:border-[#D4AF37]/70"
            />
          </label>

          <label className="block">
            <span className="mb-2 block text-sm font-semibold text-white">Note</span>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional audit note"
              className="h-11 w-full rounded-2xl border border-white/10 bg-[#090909] px-4 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-400 focus:border-[#D4AF37]/70"
            />
          </label>
        </div>
      </form>

      <div className="overflow-hidden rounded-2xl border border-[#3A3216]/80 bg-[#101010]">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="text-left text-xs font-bold uppercase tracking-wider text-slate-400">
              <tr className="border-b border-white/5">
                <th className="px-4 py-4">Member</th>
                <th className="px-4 py-4">Game</th>
                <th className="px-4 py-4 text-right">Amount</th>
                <th className="px-4 py-4 text-right">Fee</th>
                <th className="px-4 py-4">Reason</th>
                <th className="px-4 py-4">Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-white/5">
                  <td className="px-4 py-4">
                    <VerifiedName user={row} name={row.username || "-"} className="font-semibold text-white" />
                    <div className="text-xs text-slate-500">{row.email || "-"}</div>
                  </td>
                  <td className="px-4 py-4 text-slate-300">{grantGame(row)}</td>
                  <td className="px-4 py-4 text-right font-mono text-[#F1C40F]">{grantAmount(row)}</td>
                  <td className="px-4 py-4 text-right font-mono text-slate-300">{grantFee(row)}</td>
                  <td className="px-4 py-4 text-slate-300">{grantReason(row)}</td>
                  <td className="px-4 py-4 text-xs text-slate-500">{row.created_at ? new Date(row.created_at).toLocaleString("en-US") : "-"}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-slate-500">
                    No free-credit grants yet.
                  </td>
                </tr>
              )}
              {loading && rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-10 text-center text-slate-500">
                    Loading free credits...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
